import { normalizeString, Tag, TagResult } from "@tenforce/product-fields"

import { dataAPI } from "./api"
import { getDataApiPathPrefix } from "./api/data"
import { TagDTO } from "./models/fields"
import {
    ODataGetArrayResponse,
    ODataGetResourceResponse,
} from "./models/api/data"
import { formatString } from "../util/Strings"
import { RequestErrorDataApi } from "../errors"
import { Exceptions } from "../util/Translations"

const {
    AnErrorOccurredFetchingSelectedTagsFor = 'An error occurred while fetching the selected tags for ["{0}"].',
    AnErrorOccurredWhileSearchingForTag = 'An error occurred while looking for a tag with the content  ["{0}"] for ["{1}"].',
    AnErrorOccurredWhileFetchingAllTagsFor = 'An error occurred while fetching all tags for ["{0]"].',
} = Exceptions
export function parseTagData(data: TagDTO): Tag {
    return {
        id: data.Id,
        name: data.Label,
        normalized: normalizeString(data.Label),
        color: data.Color,
    }
}

export const getTagByExactName = async (
    itemId: number,
    fieldId: string,
    filter = "",
): Promise<Tag | null> => {
    return dataAPI
        .getJSON<ODataGetArrayResponse<TagDTO>>(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/TF.TagsItemField/SelectableValues`,
            {
                $filter: `Label eq '${filter}'`,
            },
        )
        .then((response) => {
            return response.value.map(parseTagData)[0]
        })
        .catch(async (error) => {
            if (error instanceof Response) {
                const errorMessage = formatString(
                    AnErrorOccurredWhileSearchingForTag,
                    filter,
                    fieldId,
                )
                throw await RequestErrorDataApi.create(
                    errorMessage,
                    error.status,
                    error,
                )
            }
            throw error
        })
}

export const getAllTags = async (
    itemId: number,
    fieldId: string,
    ignoredTags: Tag[],
    filter = "",
    skip = 0,
    top = 1000,
): Promise<TagResult> => {
    return dataAPI
        .getJSON<ODataGetArrayResponse<TagDTO>>(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/TF.TagsItemField/SelectableValues`,
            {
                $filter: `contains(Label, '${filter}') and not(Id in (${ignoredTags.map(
                    (tag) => tag.id,
                )}))`,
                $top: top,
                $skip: skip,
                $count: true,
            },
        )
        .then((response) => {
            const parsed = response.value.map(parseTagData)
            return {
                tags: parsed,
                currentCount: parsed.length,
                totalCount: response["@count"] ?? 0,
            }
        })
        .catch(async (error) => {
            if (error instanceof Response) {
                const errorMessage = formatString(
                    AnErrorOccurredWhileFetchingAllTagsFor,
                    fieldId,
                )
                throw await RequestErrorDataApi.create(
                    errorMessage,
                    error.status,
                    error,
                )
            }
            throw error
        })
}

export const getSelectedTags = async (
    itemId: number,
    fieldId: string,
): Promise<Tag[]> => {
    return dataAPI
        .getJSON<ODataGetArrayResponse<TagDTO>>(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/TF.TagsItemField/Value`,
        )
        .then((response) => {
            return response.value
                .map(parseTagData)
                .sort((a, b) => a.name.localeCompare(b.name))
        })
        .catch(async (error) => {
            if (error instanceof Response) {
                const errorMessage = formatString(
                    AnErrorOccurredFetchingSelectedTagsFor,
                    fieldId,
                )
                throw await RequestErrorDataApi.create(
                    errorMessage,
                    error.status,
                    error,
                )
            }
            throw error
        })
}

export const updateTag = async (tag: Tag): Promise<void> => {
    await dataAPI.patchJSON(`Tags/${tag.id}`, {
        Label: tag.name,
        Color: tag.color,
    })
}

export const deleteTag = async (tag: Tag): Promise<void> => {
    await dataAPI.remove(`Tags/${tag.id}`, {
        key: tag.id,
    })
}

export const createTag = async (tag: Tag): Promise<Tag> => {
    const result = await dataAPI.postJSON<ODataGetResourceResponse<TagDTO>>(
        "Tags",
        {
            Label: tag.name,
            Color: tag.color,
            isActive: true,
        },
    )

    return parseTagData(result)
}

export const sendTagPickerDeltaValue = async (
    toSelect: number[],
    toDeselect: number[],
    itemId: number,
    fieldId: string,
): Promise<void> => {
    await dataAPI.patchJSON<ODataGetArrayResponse<TagDTO>>(
        `ItemFields(ItemId=${itemId},FieldDefinitionId='${fieldId}')`,
        {
            "@type": "#TF.TagsItemField",
            "Value@delta": [
                ...toSelect.map((id) => ({
                    "@type": "#TF.Tag",
                    "@id": `${
                        window.location.origin
                    }${getDataApiPathPrefix()}/Tags/${id}`,
                })),
                ...toDeselect.map((id) => ({
                    "@removed": {
                        reason: "changed",
                    },
                    "@type": "#TF.Tag",
                    "@id": `${
                        window.location.origin
                    }${getDataApiPathPrefix()}/Tags/${id}`,
                })),
            ],
        },
    )
}
